import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DateField } from '../components/dropdown';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX } from '../styles/designSystem';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { MetricItem } from '../services/metricsService';
import { getMetrics } from '../services/metricsService';

function formatDateTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString([], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    startDate: fmt(start),
    endDate: fmt(end),
  };
}

export const MetricsPage: React.FC = () => {
  const defaults = getDefaultDateRange();
  const [items, setItems] = useState<MetricItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [process, setProcess] = useState('');
  const [processOptions, setProcessOptions] = useState<string[]>([]);

  const loadMetrics = useCallback(async (
    params: { startDate: string; endDate: string; process: string },
    signal?: AbortSignal
  ) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMetrics(
        {
          startDate: params.startDate || undefined,
          endDate: params.endDate || undefined,
          process: params.process || undefined,
        },
        signal
      );
      setItems(data);

      // Extract unique process names for the dropdown
      const names = [...new Set(data.map((m) => m.processName))].sort();
      setProcessOptions((prev) => {
        // Only update if we got a broader set (no process filter active)
        if (!params.process) return names;
        // Merge with existing options
        const merged = [...new Set([...prev, ...names])].sort();
        return merged;
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMetrics({ startDate, endDate, process }, controller.signal);
    return () => controller.abort();
  }, [loadMetrics, startDate, endDate, process]);

  const chartData = useMemo(() => {
    if (items.length === 0) return null;

    // Group by date (yyyy-mm-dd) and process, taking max duration per day per process
    const byDateProcess = new Map<string, Map<string, number>>();
    const allProcesses = new Set<string>();

    for (const item of items) {
      const date = item.dateTime.slice(0, 10);
      allProcesses.add(item.processName);

      if (!byDateProcess.has(date)) {
        byDateProcess.set(date, new Map());
      }
      const processMap = byDateProcess.get(date)!;
      const current = processMap.get(item.processName) ?? 0;
      processMap.set(item.processName, Math.max(current, item.elapsedSeconds));
    }

    const sortedDates = [...byDateProcess.keys()].sort();
    const processes = [...allProcesses].sort();

    const xAxis = sortedDates.map((d) => new Date(d));
    const series = processes.map((name) => ({
      label: name,
      data: sortedDates.map((date) => byDateProcess.get(date)?.get(name) ?? null),
      connectNulls: true,
    }));

    return { xAxis, series };
  }, [items]);

  return (
    <>
      {/* Filters */}
      <Box sx={[CARD_SX, { p: 1.5, mb: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }]}>
        <div style={{ width: 170 }}>
          <DateField label="Start Date" value={startDate || null} onChange={(iso) => setStartDate(iso ?? '')} />
        </div>
        <div style={{ width: 170 }}>
          <DateField label="End Date" value={endDate || null} onChange={(iso) => setEndDate(iso ?? '')} />
        </div>
        <TextField
          label="Process"
          select
          size="small"
          value={process}
          onChange={(e) => setProcess(e.target.value)}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">All Processes</MenuItem>
          {processOptions.map((name) => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </TextField>

        <Tooltip title="Refresh">
          <IconButton
            sx={{
              bgcolor: '#fff',
              color: '#238bdd',
              borderRadius: 1,
              border: '1px solid #238bdd',
              '&:hover': { bgcolor: 'rgba(35,139,221,0.08)' },
            }}
            onClick={() => loadMetrics({ startDate, endDate, process })}
            disabled={loading}
            size="small"
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>

        {!loading && (
          <Typography variant="caption" color="text.secondary">
            {items.length} record{items.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Duration Chart */}
      {!loading && chartData && (
        <Box sx={[CARD_SX, { mb: 1, p: 2 }]}>
          <Typography sx={{ mb: 1, textTransform: 'uppercase', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', color: '#646464' }}>
            Duration by Process
          </Typography>
          <LineChart
            height={300}
            xAxis={[{
              data: chartData.xAxis,
              scaleType: 'time',
              valueFormatter: (date: Date) =>
                date.toLocaleDateString([], { day: '2-digit', month: 'short' }),
            }]}
            yAxis={[{
              label: 'Duration (s)',
            }]}
            series={chartData.series}
            slotProps={{
              legend: {
                position: { vertical: 'top', horizontal: 'right' },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                labelStyle: { fontSize: 11 },
              },
            }}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Date/Time</DataTableHeadCell>
                <DataTableHeadCell>Process</DataTableHeadCell>
                <DataTableHeadCell>Duration</DataTableHeadCell>
                <DataTableHeadCell align="right">Source</DataTableHeadCell>
                <DataTableHeadCell align="right">Updated</DataTableHeadCell>
                <DataTableHeadCell align="right">Failed</DataTableHeadCell>
                <DataTableHeadCell align="right">Target</DataTableHeadCell>
                <DataTableHeadCell>Summary</DataTableHeadCell>
                <DataTableHeadCell>Alert</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <DataTableEmpty colSpan={9}>
                  No metrics found for the selected filters
                </DataTableEmpty>
              ) : (
                items.map((item) => (
                  <DataTableRow key={item.id}>
                    <DataTableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(item.dateTime)}
                    </DataTableCell>
                    <DataTableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300, fontSize: 13 }}>
                        {item.processName}
                      </Typography>
                    </DataTableCell>
                    <DataTableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatDuration(item.elapsedSeconds)}
                    </DataTableCell>
                    <DataTableCell align="right">
                      {item.sourceTotalRecordCount ?? '—'}
                    </DataTableCell>
                    <DataTableCell align="right">
                      {item.updatedRecordCount ?? '—'}
                    </DataTableCell>
                    <DataTableCell align="right">
                      {item.failedRecordCount > 0 ? (
                        <Chip tone="error">{item.failedRecordCount}</Chip>
                      ) : (
                        item.failedRecordCount
                      )}
                    </DataTableCell>
                    <DataTableCell align="right">
                      {item.targetTotalRecordCount ?? '—'}
                    </DataTableCell>
                    <DataTableCell muted>
                      {item.summaryInfo ? (
                        <Tooltip title={<span dangerouslySetInnerHTML={{ __html: item.summaryInfo }} />}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontSize: 13 }}>
                            {item.summaryInfo.replace(/<br\s*\/?>/gi, ' · ')}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {item.summaryAlert ? (
                        <Tooltip title={<span dangerouslySetInnerHTML={{ __html: item.summaryAlert }} />}>
                          <span>
                            <Chip tone="warning">
                              {item.alertSeverity !== '-' ? item.alertSeverity : 'Alert'}
                            </Chip>
                          </span>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
    </>
  );
};
